// utils/dataProcessing.ts
import { BuildingType } from '../../../types/BuildingTypeEnums';
import { BuildingSummary } from '../../../types/protocols';
import {
    ServiceLogEntry,
    ServiceData,
    DateRange,
    TimeGrouping,
    ServiceType,
    UserGroup,
    BuildingTypeOption
} from '../types/analysis.types';
import {
    getYearsInRange,
    getGroupKey,
    generateAllDateKeys
} from './dateUtils';

export const extractServiceLogEntries = (
    data: any,
    dateRange: DateRange,
    buildingsData: BuildingSummary[],
    buildingTypes: BuildingType[],
    selectedUserGroup: string | null,
    comments: Boolean,
): ServiceLogEntry[] => {
    if (!data || !data.servicesLogs) {
        return [];
    }

    const startDate = new Date(dateRange.start);
    const endDate = new Date(dateRange.end);
    const years = getYearsInRange(dateRange.start, dateRange.end);
    const entries: ServiceLogEntry[] = [];

    const allowedBuildingKeys = new Set(
        buildingsData
            .filter(building => buildingTypes.includes(building.buildingType || BuildingType.other))
            .map(building => building.key)
    );

    years.forEach(year => {
        const yearStr = year.toString();
        const yearLogs = data.servicesLogs[yearStr];

        if (!yearLogs) {
            return;
        }

        Object.entries(yearLogs).forEach(([serviceType, serviceLogs]: [string, any]) => {
            if (comments) {
                if (serviceType !== 'generalComment') return;
            } else {
                if (serviceType === 'generalComment') return;
            }

            Object.entries(serviceLogs).forEach(([buildingKey, locationLogs]: [string, any]) => {
                if (!allowedBuildingKeys.has(buildingKey)) {
                    return;
                }

                // Filter by user group if selected
                if (selectedUserGroup) {
                    const building = buildingsData.find(b => b.key === buildingKey);
                    if (!building || !building.responsibleGroups || !building.responsibleGroups.includes(selectedUserGroup)) {
                        return;
                    }
                }

                Object.entries(locationLogs).forEach(([logId, logEntry]: [string, any]) => {
                    const date = logEntry.dateString.split(' ')[0];
                    const logDate = new Date(date);

                    if (logDate < startDate || logDate > endDate) {
                        return;
                    }

                    entries.push({
                        id: `${year}-${serviceType}-${logId}`,
                        buildingKey,
                        user: logEntry.user || 'Unknown',
                        date: date,
                        dateString: logEntry.dateString,
                        value: logEntry.value,
                        comment: logEntry.comment,
                        serviceType: serviceType
                    });
                });
            });
        });
    });

    return entries.sort((a, b) => new Date(b.dateString).getTime() - new Date(a.dateString).getTime());
};

export const transformDataForChart = (
    entries: ServiceLogEntry[],
    grouping: TimeGrouping,
    buildings: BuildingSummary[],
    dateRange: DateRange
): ServiceData[] => {
    if (!entries || entries.length === 0) {
        return [];
    }

    // Group entries by date and building, find latest visit per building per date
    const visitsByDateAndBuilding: Record<string, Record<string, ServiceLogEntry>> = {};

    let minDate: Date | null = dateRange.start ? new Date(dateRange.start) : null;
    let maxDate: Date | null = dateRange.end ? new Date(dateRange.end) : null;

    entries.forEach((entry) => {
        const logDate = new Date(entry.date);

        if (!minDate || logDate < minDate) minDate = logDate;
        if (!maxDate || logDate > maxDate) maxDate = logDate;

        const groupKey = getGroupKey(entry.date, grouping);

        if (!visitsByDateAndBuilding[groupKey]) {
            visitsByDateAndBuilding[groupKey] = {};
        }

        const currentEntry = visitsByDateAndBuilding[groupKey][entry.buildingKey];
        if (!currentEntry || new Date(entry.dateString) > new Date(currentEntry.dateString)) {
            visitsByDateAndBuilding[groupKey][entry.buildingKey] = entry;
        }
    });

    // Group by company based on the latest visits
    const visitsByDateAndCompany: Record<string, Record<string, number>> = {};

    Object.entries(visitsByDateAndBuilding).forEach(([dateKey, buildingEntries]) => {
        visitsByDateAndCompany[dateKey] = {};

        Object.values(buildingEntries).forEach((entry) => {
            const building = buildings.find(b => b.key === entry.buildingKey);
            let companyKey = 'unknown';

            if (building && building.responsibleGroups && building.responsibleGroups.length > 0) {
                companyKey = building.responsibleGroups[0];
            }

            if (!visitsByDateAndCompany[dateKey][companyKey]) {
                visitsByDateAndCompany[dateKey][companyKey] = 0;
            }
            visitsByDateAndCompany[dateKey][companyKey]++;
        });
    });

    if (!minDate || !maxDate) {
        return [];
    }

    const allDateKeys = generateAllDateKeys(minDate, maxDate, grouping);

    // Get all unique company keys
    const allCompanyKeys = new Set<string>();
    Object.values(visitsByDateAndCompany).forEach(companies => {
        Object.keys(companies).forEach(companyKey => allCompanyKeys.add(companyKey));
    });

    return allDateKeys.map(dateKey => {
        const result: ServiceData = {
            date: dateKey,
            count: 0
        };

        const companiesForDate = visitsByDateAndCompany[dateKey] || {};
        let totalVisits = 0;

        allCompanyKeys.forEach(companyKey => {
            const visits = companiesForDate[companyKey] || 0;
            result[companyKey] = visits;
            totalVisits += visits;
        });

        result.count = totalVisits;
        return result;
    });
};

export const getServicesSummary = (
    logEntries: ServiceLogEntry[],
    serviceTypes: ServiceType[],
    serviceTitles: Record<string, string>,
    buildings: BuildingSummary[],
    categoriesOrGroups: BuildingTypeOption[] | UserGroup[],
    type: 'buildingType' | 'company'
) => {
    const serviceCounts: Record<string, {
        total: number,
        byCategory: Record<string, number>
    }> = {};

    // Determine available categories
    let availableCategories: string[] = [];
    if (type === 'buildingType') {
        availableCategories = (categoriesOrGroups as BuildingTypeOption[]).map(option => option.value as string);
    } else {
        availableCategories = ['unknown', ...(categoriesOrGroups as UserGroup[]).map(group => group.key)];
    }

    // Initialize all service types with 0 count
    serviceTypes.forEach(serviceType => {
        if (serviceType.key) {
            serviceCounts[serviceType.key] = {
                total: 0,
                byCategory: {}
            };
            availableCategories.forEach(category => {
                serviceCounts[serviceType.key!].byCategory[category] = 0;
            });
        }
    });

    // Count services from log entries
    logEntries.forEach(entry => {
        if (entry.serviceType) {
            const building = buildings.find(b => b.key === entry.buildingKey);

            let categoryKey: string;
            if (type === 'buildingType') {
                categoryKey = (building?.buildingType || BuildingType.other) as string;
            } else {
                categoryKey = 'unknown';
                if (building && building.responsibleGroups && building.responsibleGroups.length > 0) {
                    categoryKey = building.responsibleGroups[0];
                }
            }

            if (!serviceCounts[entry.serviceType]) {
                serviceCounts[entry.serviceType] = {
                    total: 0,
                    byCategory: {}
                };
                availableCategories.forEach(category => {
                    serviceCounts[entry.serviceType!].byCategory[category] = 0;
                });
            }

            serviceCounts[entry.serviceType].total++;
            serviceCounts[entry.serviceType].byCategory[categoryKey] =
                (serviceCounts[entry.serviceType].byCategory[categoryKey] || 0) + 1;
        }
    });

    // Convert to array format
    const data = Object.entries(serviceCounts)
        .map(([serviceType, counts]) => ({
            serviceType,
            serviceName: serviceTitles[serviceType] || serviceType,
            total: counts.total,
            byCategory: counts.byCategory
        }))
        .sort((a, b) => {
            if (b.total !== a.total) {
                return b.total - a.total;
            }
            return a.serviceName.localeCompare(b.serviceName);
        });

    // Prepare columns
    const columns = availableCategories.map(category => {
        let label: string;
        if (type === 'buildingType') {
            const option = (categoriesOrGroups as BuildingTypeOption[]).find(opt => (opt.value as string) === category);
            label = option ? option.label : category;
        } else {
            if (category === 'unknown') {
                label = 'Okänd';
            } else {
                const group = (categoriesOrGroups as UserGroup[]).find(g => g.key === category);
                label = group ? group.name : category;
            }
        }
        return { key: category, label };
    });

    // Prepare pie chart data
    const distribution: Record<string, number> = {};
    availableCategories.forEach(category => {
        distribution[category] = 0;
    });

    data.forEach(service => {
        Object.entries(service.byCategory).forEach(([category, count]) => {
            if (distribution.hasOwnProperty(category)) {
                distribution[category] += count;
            }
        });
    });

    const pieData = availableCategories
        .map(category => {
            const column = columns.find(col => col.key === category);
            return {
                name: column ? column.label : category,
                value: distribution[category] || 0,
                category
            };
        })
        .filter(item => type === 'company' ? item.value > 0 : true);

    return { data, columns, pieData };
};

export const getCollectedValuesSummary = (
    logEntries: ServiceLogEntry[],
    serviceTypes: ServiceType[]
) => {
    const valueCollectingServices = serviceTypes.filter(serviceType => serviceType.collectValue);

    const summaries = valueCollectingServices.map(serviceType => {
        if (!serviceType.key) return null;

        const serviceEntries = logEntries.filter(entry => entry.serviceType === serviceType.key);
        const totalValue = serviceEntries.reduce((sum, entry) => sum + (entry.value || 0), 0);

        return {
            serviceType: serviceType.key,
            serviceName: serviceType.title || serviceType.key,
            totalValue,
            unit: serviceType.unit || '',
            entryCount: serviceEntries.length
        };
    }).filter(Boolean);

    return summaries.sort((a: any, b: any) => {
        if (b.totalValue !== a.totalValue) {
            return b.totalValue - a.totalValue;
        }
        return a.serviceName.localeCompare(b.serviceName);
    });
};